import { API } from "@/app/api/BaseAPI";
import {Convert, UserEntity} from "@/app/entity/UserEntity"

/**
 * One game identity of the logged-in account on one server (18-CONTRACT.md §8, `identities.list`).
 * `is_server_owner` means the account owns that server; unlinking then keeps ownership.
 */
export interface LinkedIdentity {
  server_uuid: string
  server_name: string
  minecraft_uuid: string
  minecraft_name: string
  linked_at: string
  is_server_owner: boolean
}

/** Result of unlinking (18-CONTRACT.md §8, `identities.unlink`). `authorization_removed` is false for an owner. */
export interface UnlinkIdentityResult {
  outcome: 'unlinked'
  authorization_removed: boolean
}

/**
 * A class for calling user api.
 *
 * The documentation of the api are below:
 * - [Get user information](https://wisdomme.gitbook.io/ultikits-panel/get-info-1/get-user-info)
 * - [Add user](https://wisdomme.gitbook.io/ultikits-panel/get-info-1/add-user-info-register)
 * - [Update user](https://wisdomme.gitbook.io/ultikits-panel/get-info-1/update-user-info)
 * - [Delete user](https://wisdomme.gitbook.io/ultikits-panel/get-info-1/delete-user)
 *
 * You can use this api to get, add, update, delete user.
 *
 * This api requires authentication, so you need to ensure that the user is logged in before using it.
 *
 * Some operations of this api require admin permission, so you need to ensure that the user has admin permission before using it.
 *
 * It based on the BaseAPI, so you can use the BaseAPI to do more operations.
 *
 * @class UserAPI
 * @author qianmo
 * @date 2023-07-02 20:56
 * @version 2.0.0
 * @since 2.0.0
 */
export class UserAPI {
  /** The endpoint of user api, which is used to get user information. */
  readonly userPath = "/user/"
  /** The endpoint of user api, which is used to add user. */
  readonly addPath = "/user/adduser"
  /** The endpoint of user api, which is used to update user. */
  readonly updatePath = "/user/updateuser/"
  /** The endpoint of user api, which is used to delete user. */
  readonly deletePath = "/user/deluser/"

  /**
   * Get user information
   *
   * You can use this function to get user information
   *
   * It will return a promise of UserEntity which contains the user information.
   *
   * The user information is an object of UserEntity.
   * @see UserEntity
   *
   * @param id user id
   *
   * @returns {Promise<any>} a promise of UserEntity
   */
  getUser(id: number|string): Promise<UserEntity> {
    const api = new API();

    return new Promise((resolve, reject) => {
      // 验证 ID 是否有效
      if (!id || id === 'undefined' || id === 'null' || id === 0 || id === '0') {
        reject(new Error('Invalid user ID: ' + id));
        return;
      }

      api
        .withPath(this.userPath + id)
        .withMethod("POST")
        .withToken()
        .request()
        .then((response) => {
          resolve(Convert.toUserEntity(response.data));
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Get user information with a given field
   *
   * This function is used to get user information with a specific field.
   *
   * @param id user id
   * @param field field name
   *
   * @returns {Promise<any>} a promise of user information, null if not found.
   */
  getUserField(id: number|string, field: string): Promise<string> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(this.userPath + id)
        .withParams({ field })
        .withMethod("POST")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Add user (register)
   *
   * user use this function to register
   *
   * It will return server response.
   *
   * There are three types of response:
   * - 200: success, the response looks like this: `{"result": "true"; "msg": "Operate successfully"}`
   * - 403: failed, the response is empty
   * - 500: failed, Internal Server Error
   *
   * @param username username
   * @param password password
   * @param email email
   *
   * @returns {Promise<any>} a promise of server response
   */
  addUser(username: string, password: string, email: string): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      // 创建 FormData 对象
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);
      formData.append('email', email);

      api
        .withPath(this.addPath)
        .withBody(formData)
        .withMethod("POST")
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Update user information
   *
   * You can use this function to update a specific field of user information.
   *
   * So you can only update one field at a time.
   *
   * If success, the response looks like this: `{"code": 200, "msg": "Operate successfully!"}`
   *
   * @param id user id
   * @param username username
   * @param field field name
   * @param value field value
   *
   * @returns {Promise<any>} a promise of server response
   */
  updateUser(id: number|string, username: string, field: string, value: string): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(this.updatePath + id)
        .withParams({ [field]: value })
        .withMethod("POST")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Delete user
   *
   * You can use this function to delete user.
   *
   * **DANGER** You can only use this with highest permission.
   *
   * NEVER EVER USE THIS IF IT IS NOT NECESSARY.
   *
   * User information is protected under Information Privacy Law.
   *
   * @param id user id you want to delete
   */
  deleteUser(id: number|string): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(this.deletePath + id)
        .withMethod("POST")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Validate email
   *
   * You can use this function to validate email.
   *
   * code is optional. When sending a code, leave it null and when validating a code, pass the code.
   *
   * There is a 60 seconds CD (Cool Down) for sending a code, so you need to show the user a timer.
   *
   * It will return a promise of any type.
   *
   * If success, the response will be empty.
   *
   * @param id user id
   * @param code validation code, which sent to user's email
   *
   * @return Promise<any>
   */
  validateEmail(id: string|number, code?: string|number): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.userPath}${id}/email/validate`)
        .withParams(code ? { code: code } : {})
        .withMethod("POST")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Verify email - New email verification and update API
   *
   * This function supports two operations:
   * 1. Send verification code: when only email is provided
   * 2. Verify and update email: when both email and code are provided
   *
   * @param email - The email address to verify
   * @param code - Optional verification code (if provided, will verify and update email)
   *
   * @returns Promise<any>
   */
  verifyEmail(email: string, code?: string): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      // 创建请求参数
      const params: any = { email };
      if (code) {
        params.code = code;
      }

      api
        .withPath("/user/verify-email")
        .withParams(params)
        .withMethod("POST")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * List the logged-in account's game identities, one per server (`GET /user/identities`).
   *
   * A Worker that predates the identity model answers 404 for this path; callers treat that as
   * "not available yet", not as an error.
   */
  listIdentities(): Promise<LinkedIdentity[]> {
    return new API()
      .withPath("/user/identities")
      .withMethod("GET")
      .withToken()
      .request()
      .then((response) => {
        const identities = (response.data?.data ?? response.data)?.identities;
        if (!Array.isArray(identities)) {
          throw new Error("Unexpected identities response");
        }
        return identities as LinkedIdentity[];
      });
  }

  /**
   * Unlink the logged-in account's game identity on one server (`DELETE /user/identities/{serverUuid}`).
   * For a regular player the Worker also removes that server's panel access; an owner keeps ownership.
   * There is no transfer: moving an identity is unlink here, then relink from the other account.
   */
  unlinkIdentity(serverUuid: string): Promise<UnlinkIdentityResult> {
    return new API()
      .withPath("/user/identities/" + encodeURIComponent(serverUuid))
      .withMethod("DELETE")
      .withToken()
      .request()
      .then((response) => (response.data?.data ?? response.data) as UnlinkIdentityResult);
  }
}
